<template>
  <div class="doc-editor-wrapper">
    <!-- Toolbar -->
    <div v-if="editor" class="doc-toolbar">
      <div class="doc-format-group">
        <button
          :class="{ active: editor.isActive('bold') }"
          title="粗體 (Ctrl+B)"
          @click="editor.chain().focus().toggleBold().run()"
        ><strong>B</strong></button>

        <button
          :class="{ active: editor.isActive('italic') }"
          title="斜體 (Ctrl+I)"
          @click="editor.chain().focus().toggleItalic().run()"
        ><em>I</em></button>

        <button
          :class="{ active: editor.isActive('underline') }"
          title="底線 (Ctrl+U)"
          @click="editor.chain().focus().toggleUnderline().run()"
        ><u>U</u></button>
      </div>

      <button
        :class="{ active: editor.isActive('heading', { level: 1 }) }"
        title="標題 1"
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >H1</button>

      <button
        :class="{ active: editor.isActive('heading', { level: 2 }) }"
        title="標題 2"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >H2</button>

      <button
        :class="{ active: editor.isActive('bulletList') }"
        title="項目清單"
        @click="editor.chain().focus().toggleBulletList().run()"
      >• 清單</button>

      <button
        :class="{ active: editor.isActive('orderedList') }"
        title="有序清單"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >1. 清單</button>

      <button
        :class="{ active: editor.isActive('strike') }"
        title="刪除線"
        @click="editor.chain().focus().toggleStrike().run()"
      ><s>S</s></button>

      <button
        title="分割線"
        @click="editor.chain().focus().setHorizontalRule().run()"
      >—</button>

      <select
        class="doc-font-size-select"
        title="字體大小"
        @change="applyFontSize($event)"
      >
        <option value="" disabled selected>字體大小</option>
        <option value="12px">12</option>
        <option value="14px">14</option>
        <option value="16px">16</option>
        <option value="18px">18</option>
        <option value="20px">20</option>
        <option value="24px">24</option>
        <option value="32px">32</option>
      </select>

      <div class="doc-color-group">
        <button
          class="doc-color-main"
          title="字體顏色"
          @click="applyColor(lastColor)"
        >
          <span class="color-icon">
            A
            <i class="color-icon-bar" :style="{ backgroundColor: lastColor }"></i>
          </span>
        </button>
        <button
          class="doc-color-toggle"
          title="選擇顏色"
          @click="showColorPicker = !showColorPicker"
        >▾</button>

        <div v-if="showColorPicker" class="doc-color-popover">
          <button
            v-for="hex in fontColors"
            :key="hex"
            class="doc-color-swatch"
            :style="{ backgroundColor: hex }"
            @click="applyColor(hex)"
          ></button>
          <input
            type="color"
            class="doc-color-custom"
            title="自訂顏色"
            @input="applyColor($event.target.value)"
          />
        </div>
      </div>

      <div class="doc-align-group">
        <button
          :class="{ active: editor.isActive({ textAlign: 'left' }) }"
          title="靠左對齊"
          @click="editor.chain().focus().setTextAlign('left').run()"
        ><span class="align-icon align-icon--left"><i></i><i></i><i></i></span></button>

        <button
          :class="{ active: editor.isActive({ textAlign: 'center' }) }"
          title="置中對齊"
          @click="editor.chain().focus().setTextAlign('center').run()"
        ><span class="align-icon align-icon--center"><i></i><i></i><i></i></span></button>

        <button
          :class="{ active: editor.isActive({ textAlign: 'right' }) }"
          title="靠右對齊"
          @click="editor.chain().focus().setTextAlign('right').run()"
        ><span class="align-icon align-icon--right"><i></i><i></i><i></i></span></button>

        <button
          :class="{ active: editor.isActive({ textAlign: 'justify' }) }"
          title="左右對齊"
          @click="editor.chain().focus().setTextAlign('justify').run()"
        ><span class="align-icon align-icon--justify"><i></i><i></i><i></i></span></button>
      </div>
    </div>

    <editor-content :editor="editor" class="doc-content" />

    <!-- 連線狀態提示 -->
    <div class="doc-status">
      <span v-if="isConnected" class="status-online">● 已連線</span>
      <span v-else class="status-offline">● 連線中…</span>
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCursor from "@tiptap/extension-collaboration-cursor";
import * as Y from "yjs";
import { HocuspocusProvider } from "@hocuspocus/provider";
import TextStyle from "@tiptap/extension-text-style";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import Underline from "@tiptap/extension-underline";
import FontSize from "../tiptap/FontSize";

// 每位使用者的游標顏色從這個色盤隨機選取
const CURSOR_COLORS = [
  "#f97316", "#8b5cf6", "#06b6d4", "#10b981",
  "#f43f5e", "#3b82f6", "#eab308", "#ec4899",
];

function randomColor() {
  return CURSOR_COLORS[Math.floor(Math.random() * CURSOR_COLORS.length)];
}

export default {
  name: "DocEditor",
  components: { EditorContent },

  props: {
    roomId: { type: String, required: true },
    userName: { type: String, required: true },
    hocuspocusUrl: { type: String, required: true },
  },

  data() {
    return {
      editor: null,
      provider: null,
      ydoc: null,
      isConnected: false,
      typingTimer: null,
      typingStartTime: null,
      isTyping: false,
      cursorColor: randomColor(),
      fontColors: ["#000000", "#e03131", "#1971c2", "#2f9e44", "#f08c00", "#9c36b5", "#868e96", "#7c4a03"],
      lastColor: "#000000",
      showColorPicker: false,
    };
  },

  watch: {
    userName(newName) {
      if (this.provider) {
        this.provider.setAwarenessField("user", {
          name: newName || "匿名",
          color: this.cursorColor,
        });
      }
    },
  },

  mounted() {
    this.ydoc = new Y.Doc();

    // 建立 Hocuspocus WebSocket 連線（document name = roomId）
    this.provider = new HocuspocusProvider({
      url: this.hocuspocusUrl,
      name: this.roomId,
      document: this.ydoc,
      onConnect: () => { this.isConnected = true; },
      onDisconnect: () => { this.isConnected = false; },
    });

    // 建立 TipTap 編輯器
    this.editor = new Editor({
      extensions: [
        StarterKit.configure({ history: false }),
        Collaboration.configure({ document: this.ydoc }),
        CollaborationCursor.configure({
          provider: this.provider,
          user: { name: this.userName || "匿名", color: this.cursorColor },
        }),
        TextStyle,
        FontSize,
        Color,
        Underline,
        TextAlign.configure({ types: ["heading", "paragraph"] }),
      ],
      onUpdate: ({ editor }) => {
        // 通知 App.vue 最新文字內容
        this.$emit("content-update", editor.getText());
        this._handleTypingDetection();
      },
    });
  },

  methods: {
    // Typing 偵測：
    //   - 第一次收到 update → 視為 typing start，emit typing-start
    //   - 每次收到 update → 重置 5 秒計時器
    //   - 5 秒內沒有新 update → 視為 typing end，emit typing-end
    _handleTypingDetection() {
      if (!this.isTyping) {
        this.isTyping = true;
        this.typingStartTime = new Date().toISOString();
        this.$emit("typing-start", this.typingStartTime);
      }
      clearTimeout(this.typingTimer);
      this.typingTimer = setTimeout(() => {
        const endTime = new Date().toISOString();
        const duration = Math.round(
          (new Date(endTime) - new Date(this.typingStartTime)) / 1000
        );
        this.$emit("typing-end", {
          startTime: this.typingStartTime,
          endTime,
          duration,
        });
        this.isTyping = false;
        this.typingStartTime = null;
      }, 5000);
    },

    applyFontSize(event) {
      const size = event.target.value;
      if (!size) return;
      this.editor.chain().focus().setFontSize(size).run();
      event.target.value = ""; // 重設為 placeholder，做成一次性動作選單
    },

    applyColor(hex) {
      this.editor.chain().focus().setColor(hex).run();
      this.lastColor = hex;
      this.showColorPicker = false;
    },
  },

  beforeDestroy() {
    clearTimeout(this.typingTimer);
    if (this.editor) this.editor.destroy();
    if (this.provider) this.provider.destroy();
    if (this.ydoc) this.ydoc.destroy();
  },
};
</script>

<style scoped>
.doc-editor-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

/* Toolbar */
.doc-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 10px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  flex-shrink: 0;
}

.doc-toolbar button {
  padding: 3px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  min-width: 36px;
  transition: background 0.1s, color 0.1s;
}

.doc-toolbar button:hover {
  background: #e9ecef;
}

.doc-toolbar button.active {
  background: #0d6efd;
  color: #fff;
  border-color: #0d6efd;
}

.doc-font-size-select {
  padding: 3px 6px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
}

/* 粗體/斜體/底線 */
.doc-format-group {
  display: flex;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
}

.doc-format-group button {
  padding: 3px 10px;
  border: none;
  border-right: 1px solid #ced4da;
  border-radius: 0;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
  min-width: 32px;
  transition: background 0.1s, color 0.1s;
}

.doc-format-group button:last-child {
  border-right: none;
}

.doc-format-group button:hover {
  background: #e9ecef;
}

.doc-format-group button.active {
  background: #0d6efd;
  color: #fff;
}

/* 字體顏色 */
.doc-color-group {
  position: relative;
  display: flex;
  border: 1px solid #ced4da;
  border-radius: 4px;
}

.doc-color-main,
.doc-color-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: #fff;
  cursor: pointer;
}

.doc-color-main {
  padding: 3px 8px;
  border-right: 1px solid #ced4da;
  border-radius: 4px 0 0 4px;
  font-size: 13px;
  font-weight: 700;
}

.doc-color-toggle {
  padding: 3px 6px;
  border-radius: 0 4px 4px 0;
  font-size: 10px;
  color: #495057;
}

.doc-color-main:hover,
.doc-color-toggle:hover {
  background: #e9ecef;
}

.color-icon {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
}

.color-icon-bar {
  display: block;
  width: 14px;
  height: 3px;
  margin-top: 2px;
  border-radius: 1px;
}

.doc-color-popover {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.doc-color-swatch {
  width: 18px;
  height: 18px;
  min-width: 18px;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 3px;
  cursor: pointer;
}

/* 對齊按鈕 */
.doc-align-group {
  display: flex;
  border: 1px solid #ced4da;
  border-radius: 4px;
  overflow: hidden;
}

.doc-align-group button {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  padding: 5px 8px;
  border: none;
  border-right: 1px solid #ced4da;
  border-radius: 0;
  background: #fff;
  cursor: pointer;
  transition: background 0.1s;
}

.doc-align-group button:last-child {
  border-right: none;
}

.doc-align-group button:hover {
  background: #e9ecef;
}

.doc-align-group button.active {
  background: #0d6efd;
}

.doc-align-group button.active .align-icon i {
  background: #fff;
}

.align-icon {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 16px;
}

.align-icon i {
  display: block;
  height: 2px;
  border-radius: 1px;
  background: #495057;
}

.align-icon--left i {
  align-self: flex-start;
}
.align-icon--left i:nth-child(1) { width: 100%; }
.align-icon--left i:nth-child(2) { width: 65%; }
.align-icon--left i:nth-child(3) { width: 85%; }

.align-icon--center i {
  align-self: center;
}
.align-icon--center i:nth-child(1) { width: 100%; }
.align-icon--center i:nth-child(2) { width: 65%; }
.align-icon--center i:nth-child(3) { width: 85%; }

.align-icon--right i {
  align-self: flex-end;
}
.align-icon--right i:nth-child(1) { width: 100%; }
.align-icon--right i:nth-child(2) { width: 65%; }
.align-icon--right i:nth-child(3) { width: 85%; }

.align-icon--justify i {
  width: 100%;
}

.doc-color-custom {
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #ced4da;
  border-radius: 3px;
  cursor: pointer;
}

/* 編輯內容區 */
.doc-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  font-size: 15px;
  line-height: 1.7;
  text-align: left; /* 覆蓋 #app 的 text-align: center，避免段落預設被繼承置中 */
}

/* ProseMirror 編輯器本體 */
.doc-content :deep(.ProseMirror) {
  min-height: 300px;
  outline: none;
}

.doc-content :deep(.ProseMirror p) {
  margin: 0 0 10px;
}

.doc-content :deep(.ProseMirror h1) {
  font-size: 1.6em;
  font-weight: 700;
  margin: 0 0 12px;
}

.doc-content :deep(.ProseMirror h2) {
  font-size: 1.3em;
  font-weight: 600;
  margin: 0 0 10px;
}

.doc-content :deep(.ProseMirror ul),
.doc-content :deep(.ProseMirror ol) {
  padding-left: 20px;
  margin: 0 0 10px;
}

.doc-content :deep(.collaboration-cursor__caret) {
  position: relative;
  border-left: 2px solid;
  border-right: 2px solid;
  margin-left: -1px;
  margin-right: -1px;
  word-break: normal;
  pointer-events: none;
}

.doc-content :deep(.collaboration-cursor__label) {
  position: absolute;
  top: 0;
  left: -1px;
  transform: translateY(-100%);
  border-radius: 3px 3px 3px 0;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 5px;
  pointer-events: none;
  user-select: none;
  white-space: nowrap;
}

/* 連線狀態列 */
.doc-status {
  padding: 4px 10px;
  font-size: 11px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
  flex-shrink: 0;
}

.status-online {
  color: #198754;
}

.status-offline {
  color: #6c757d;
}
</style>
